import React, { useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { event } from '@uhg-abyss/web/tools/event';
import { storage } from '@uhg-abyss/web/tools/storage';
import { IconMaterial } from '@uhg-abyss/web/ui/IconMaterial';
import { styled } from '@uhg-abyss/web/tools/styled';
import { ExitLinkModal } from './ExitLinkModal';
import { ExitLinkEnhancer } from './ExitLinkEnhancer';

const StyledLaunchIcon = styled(IconMaterial, {
  verticalAlign: 'text-bottom',
  marginLeft: 3,
});

const retrieveName = (data) => {
  if (typeof data === 'object') {
    if (Array.isArray(data)) {
      return retrieveName(data[0]);
    }
    return retrieveName(data.props.children);
  }
  return data;
};

export const ExitLink = ({
  children,
  to,
  modalContent,
  buttonContent,
  isActive,
  className,
  exitLinkClassName,
  customIcon,
  'data-testid': dataTestid,
  before,
  after,
  enhancerPadding,
  customAdobeEvent,
  shouldAdobeExitEvent,
}) => {
  const eventFired = useRef(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const storageKey = `ExitLink-${to}`;
  const allowModalOpen = modalContent && !storage.local.get(storageKey);

  const handleClick = (e) => {
    if (!eventFired.current) {
      const name = retrieveName(children);
      if (customAdobeEvent) {
        // when custom adobe event needs to be triggered (ex: interaction)
        event(`${customAdobeEvent.name}`, customAdobeEvent.data);

        // when exitLink needs to be triggered alongside a custom adobe event
        if (shouldAdobeExitEvent) {
          event('EXIT_LINK_NAVIGATE', { linkText: name, linkUrl: to });
        }
      } else {
        event('EXIT_LINK_NAVIGATE', { linkText: name, linkUrl: to });
      }
      eventFired.current = true;
    }

    if (allowModalOpen) {
      e.stopPropagation();
      e.preventDefault();
      setModalOpen(true);
    }
  };

  return (
    <Choose>
      <When condition={allowModalOpen}>
        <ExitLinkModal
          to={to}
          isOpen={isActive || isModalOpen}
          onClose={() => setModalOpen(false)}
          canClose={!isActive}
          modalContent={modalContent}
          buttonContent={buttonContent}
          storageKey={storageKey}
          dataTestid={dataTestid}
        />
        <div className={className}>
          {React.cloneElement(children, { onClick: handleClick })}
          <Choose>
            <When condition={customIcon}>{customIcon}</When>
            <Otherwise>
              <StyledLaunchIcon size={18} color='white' />
            </Otherwise>
          </Choose>
        </div>
      </When>
      <Otherwise>
        <ExitLinkEnhancer
          to={to}
          className={exitLinkClassName}
          handleClick={handleClick}
          dataTestid={dataTestid}
          padding={enhancerPadding}
          before={before}
          after={after}
        >
          {children}
        </ExitLinkEnhancer>
      </Otherwise>
    </Choose>
  );
};

ExitLink.propTypes = {
  children: PropTypes.node.isRequired,
  to: PropTypes.string.isRequired,
  modalContent: PropTypes.node,
  buttonContent: PropTypes.node,
  isActive: PropTypes.bool,
  className: PropTypes.string,
  exitLinkClassName: PropTypes.string,
  customIcon: PropTypes.node,
  'data-testid': PropTypes.string,
  before: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  after: PropTypes.oneOfType([PropTypes.node, PropTypes.func]),
  enhancerPadding: PropTypes.number,
  customAdobeEvent: PropTypes.shape(PropTypes.object),
  shouldAdobeExitEvent: PropTypes.bool,
};

ExitLink.defaultProps = {
  isActive: false,
  modalContent: null,
  buttonContent: null,
  className: null,
  exitLinkClassName: null,
  customIcon: null,
  'data-testid': null,
  before: null,
  after: null,
  enhancerPadding: '5px',
  customAdobeEvent: false,
  shouldAdobeExitEvent: false,
};
